import { Component, computed, effect, ElementRef, HostListener, Inject, input, NgZone, OnDestroy, OnInit, Optional, output, Renderer2, signal, untracked, viewChild } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { Capacitor } from '@capacitor/core';
import { TranslateService } from '@ngx-translate/core';
import Hls, { Level } from 'hls.js';
import * as yaml from 'js-yaml';
import Plyr from 'plyr';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { catchError, delay, take, tap } from 'rxjs/operators';
import { PLYR_DEFAULT_DRIVER, REGEX_LIST, SETTINGS_SERVICE, STORAGE_KEYS } from '../../constants';
import { AutoplayConfig, ImageModel, SettingsModel, StorageKeys, VideoModel } from '../../models';
import { ProviderType } from '../../models/enums/provider-type.enum';
import { InteractiveVideoModel } from '../../models/interactive-video.model';
import { BaseSettingsGetService, LanguageStorageService, ProfileService, StorageService } from '../../services';
import { CloudImageService } from '../../services/cloud-image.service';
import { EmbededVideoHelperService } from '../../services/embeded-video-helper.service';
import { VideoProgressTrackerService } from '../../services/video-progress-tracker.service';
import { VimeoPlayerComponent } from '../vimeo-player/vimeo-player.component';
import { YoutubePlayerComponent } from '../youtube-player/youtube-player.component';

const DEFAULT_CONTROLS = [
  'play-large', 'play', 'current-time', 'progress', 'duration', 'mute', 'volume', 'settings', 'pip', 'fullscreen'
];

enum VideoProvider {
  DEFAULT = 'default-provider',
  HLS = 'hls-provider'
}

@Component({
    selector: 'lib-video',
    templateUrl: './video.component.html',
    styleUrls: ['./video.component.scss'],
    standalone: false
})
export class VideoComponent implements OnInit, OnDestroy {

  public videoPlayer = viewChild<ElementRef<HTMLVideoElement>>('videoPlayer');
  public vimeoPlayer = viewChild(VimeoPlayerComponent);
  public youtubePlayer = viewChild(YoutubePlayerComponent);

  public containerHeight = input<number, number>(0, {
    transform: (value) => value == null ? 0 : Math.max(0, value),
  });

  public fitContainer = input(false);

  public src = input<string>();
  public video = input<VideoModel>();
  public showThumbnailsForEmbed = input(false);
  public useCustomThumbnailForEmbed = input(false);
  public hideControls = input(false);
  public additionalOptions = input<Partial<Plyr.Options>>();
  public volume = input(.8);
  public type = input<string>();
  public clickToPlay = input(true);
  public relatedEntityId = input<string>();
  public x5Attributes = input(false);
  public loop = input(false);
  public controls = input<string[]>(DEFAULT_CONTROLS);
  public requiredToWatch = input(false);
  public completed = input(false);
  public interactive = input<InteractiveVideoModel>();
  public embedVideo = input<any>();
  public poster = input<Partial<ImageModel>>();
  public autoplay = input<boolean | AutoplayConfig>(false);

  public started = output<void>();
  public watched = output<void>();
  public ended = output<void>();
  public paused = output<void>();
  public videoDurationInitialized = output<number>();
  public videoCanPlay = output<void>();
  public mediaRatioLoaded = output<string | void>();
  public toggleFullscreen = output<void>();

  @HostListener('window:resize') onResize(): void {
    if (this.resizerTimeout) {
      clearTimeout(this.resizerTimeout);
    }

    this.resizerTimeout = setTimeout(() => {
      this.setVideoPlayerSize();
    }, 100);
  }

  public showPlayer = signal(true);
  public provider = computed(() => (this.src()?.includes('.m3u8') && Hls.isSupported()) ? VideoProvider.HLS : VideoProvider.DEFAULT);

  public videoConversionInProgress = false;
  public maxEmbedWidth!: number;
  public youtubeVideoId!: string;
  public embed!: { id: string; hash?: string; start?: number; provider: string } | undefined;
  public embedThumbnail?: Observable<SafeUrl>;
  public plyr!: Plyr;
  public videoSources!: Plyr.Source[];
  public videoTracks: Plyr.Track[] = [];
  public options!: Plyr.Options;
  public plyrVideoWidth = 0;
  public interactiveData?: InteractiveVideoModel;
  public nextKeyFrame: any;
  public isKeyFrameActive = false;
  public warningMessage!: string;
  public isAdmin = false;
  public initialized = new BehaviorSubject(false);
  public videoWatched = false;
  public userStartWatchingVideo = false;
  public preventVideoLoad: boolean;

  private nextKeyFrameDelay!: Subscription;
  private nextKeyFrameTimeout!: ReturnType<typeof setTimeout>;
  private allowChangingVideoPlaybackSpeed = true;
  private contentLanguage: string;
  private settingsLoaded: boolean;
  private progress: BehaviorSubject<number> = new BehaviorSubject(0);
  private resizerTimeout!: ReturnType<typeof setTimeout>;
  private _poster?: Partial<ImageModel>;

  private activeVideoProvider = VideoProvider.DEFAULT;
  private videoCompleted = signal(false);

  private autoplayConfig: AutoplayConfig = {
    enabled: false
  };


  private videoMaxResolutions = signal<{ width: number, height: number } | null>(null);

  private hls: Hls | null = null;

  private readonly defaultHlsBandwidthEstimate = 5000000;
  private readonly hlsBandwidthEstimateSaveInterval = 5000;
  private initializeTimeout?: ReturnType<typeof setTimeout>;

  private readonly managedVideoTrackAttribute = 'data-lms-video-track';

  private readonly conversionInProgressWarning = 'COMMON.video_conversion_in_progress';

  constructor(
    @Inject(PLYR_DEFAULT_DRIVER) @Optional() public defaultDriver: { updateSource },
    @Inject(REGEX_LIST) public regexList: any,
    @Inject(SETTINGS_SERVICE) private readonly settingsService: BaseSettingsGetService,
    @Inject(STORAGE_KEYS) private readonly storageKeys: StorageKeys,
    private readonly languageStorageService: LanguageStorageService,
    private readonly translateService: TranslateService,
    private readonly embededVideoHelperService: EmbededVideoHelperService,
    private readonly element: ElementRef,
    private readonly profileService: ProfileService,
    private readonly videoProgressTrackerService: VideoProgressTrackerService,
    private readonly storageService: StorageService,
    private readonly renderer: Renderer2,
    private readonly cloudImageService: CloudImageService,
    private readonly ngZone: NgZone
  ) {
    effect(() => {
      this.videoCompleted.set(this.completed());
    });

    effect(() => {
      const value = this.interactive();

      this.interactiveData = value?.active
        ? {
          active: value.active,
          script: this.parseScript(value.script)
        } : undefined;
    });

    effect(() => {
      const value = this.embedVideo();

      this.embed = value && this.getVideoDataByUrl(value.url || value);

      if (!this.embed) {
        this.embedThumbnail = undefined;
        return;
      }

      this.embedThumbnail = this.embededVideoHelperService.getVideoThumbnail(this.embed.provider as ProviderType, this.embed.id);
      this.initialized.next(this.settingsLoaded);
    });

    effect(() => {
      this._poster = this.poster();
      this.updatePoster();
    });

    effect(() => {
      this.autoplayConfig = this.normalizeAutoplay(this.autoplay());

      if (this.plyr && this.initialized.value) {
        this.initializePlaybackState(false);
      }
    });

    effect(() => {
      this.video();
      this.setupVideoTracks();
    });

    effect(() => {
      this.src();
    
      untracked(() => {
        const provider = this.provider();
        const sameProvider = this.activeVideoProvider === provider;
        this.activeVideoProvider = provider;

        if (!sameProvider) {
          clearTimeout(this.initializeTimeout);
          this.resetPlayer();
          return;
        }

        this.scheduleInitialization();
      });
    });

    effect(() => {
      this.videoPlayer();

      untracked(() => {
        this.scheduleInitialization();
      });
    });

    effect(() => {
      const containerHeight = this.containerHeight();
      const videoMaxResolutions = this.videoMaxResolutions();
      const fitContainer = this.fitContainer();

      if (fitContainer) {
        const width = this.getExplicitContainerWidth() ?? 0;
        const height = this.getExplicitContainerHeight() ?? containerHeight;

        if (width && height) {
          this.setVideoCssProperties(width, height);
          return;
        }
      }

      if (!videoMaxResolutions) {
        this.setVideoCssProperties(0, this.containerHeight());
        return;
      }

      let { width, height } = videoMaxResolutions;

      const viewportHeight = this.getExplicitContainerHeight() || containerHeight || (window.innerHeight || document.documentElement.clientHeight) * 0.9;

      if (viewportHeight < height) {
        height = Math.ceil(viewportHeight);
        width = Math.floor((videoMaxResolutions.width * height) / videoMaxResolutions.height);
      }

      this.setVideoCssProperties(width, height);
    })
  }

  ngOnInit(): void {
    this.preventVideoLoad = this.storageService.getItem(this.storageKeys.PREVENT_VIDEO_LOAD) ?? false;

    this.settingsService.getSettings()
    .pipe(
      catchError(() => of(null))
    )
    .subscribe(async settings => {
      await this.initialize(settings);
    });
  }

  public hideWarningMessage(): void {
    this.warningMessage = '';
  }

  public startVideo(): void {
    if (this.plyr?.paused) {
      this.plyr.play();
    } else if (this.youtubePlayer()) {
      this.youtubePlayer()!.playVideo();
    } else if (this.vimeoPlayer()) {
      this.vimeoPlayer()!.play();
    }
  }

  public end(enableConrols = true): void {
    if (enableConrols) {
      setTimeout(() => {
        this.enableVideoProgressControl();
      }, 1000);
    }

    this.videoCompleted.set(true);
    this.markVideoAsWatched();
    this.ended.emit();
    if (this.loop()) {
      this.startVideo();
    }
  }

  public keyFrameAction(keyframe: any): void {
    if (!keyframe) {
      return;
    }

    if (this.nextKeyFrameTimeout) {
      clearTimeout(this.nextKeyFrameTimeout);
    }

    const { action } = keyframe;

    switch (action.type || action) {
      case 'gotoframe':
        this.plyr.currentTime = action.timestamp;
        this.startVideo();
        this.isKeyFrameActive = false;
        this.keyFramesHandler();
        break;
      case 'stop':
        this.pauseVideo();
        this.watched.emit();
        break;
      case 'decision':
        if (keyframe.pause) {
          this.pauseVideo();
        }

        if (keyframe.timeout) {
          this.nextKeyFrameTimeout = setTimeout(() => {
            const defaultDecisionIndex = this.nextKeyFrame.defaultDecision;
            this.keyFrameAction(keyframe.decisions?.[defaultDecisionIndex]?.action);
          }, keyframe.timeout * 1000)
        }
        break;
    }
  }

  public videoStarted(): void {
    if (!this.userStartWatchingVideo) {
      this.userStartWatchingVideo = true;

      this.started.emit();
    }
  }

  public resetVideoTime(): void {
    if (this.plyr && this.plyr.paused) {
      this.plyr.currentTime = 0;
    }

    if (this.youtubePlayer()) {
      this.youtubePlayer()!.resetVideoTime();
    }

    if (this.vimeoPlayer()) {
      this.vimeoPlayer()!.resetVideoTime();
    }
  }

  public pauseVideo(): void {
    if (this.plyr && this.plyr.playing) {
      this.plyr.pause();
    } else if (this.youtubePlayer()) {
      this.youtubePlayer()!.pauseVideo();
    } else if (this.vimeoPlayer()) {
      this.vimeoPlayer()?.pause();
    }
  }

  public exitFullsceen(): void {
    if (this.plyr?.fullscreen?.active) {
      this.plyr.fullscreen.exit();
    }

    if (this.vimeoPlayer()) {
      this.vimeoPlayer()!.exitFullscreen();
    }

    if (this.youtubePlayer()) {
      this.youtubePlayer()!.exitFullscreen();
    }
  }

  private async initialize(settings: SettingsModel | null = null): Promise<void> {
    this.settingsLoaded = true;
    this.isAdmin = this.profileService.isCurrentUserIsAdmin();
    this.allowChangingVideoPlaybackSpeed = settings?.lesson?.allowChangingVideoPlaybackSpeed ?? false;

    this.contentLanguage = settings?.languages?.contentLanguageEnabled
      ? this.profileService.getCurrentProfileValue()?.contentLanguage ?? this.translateService.getCurrentLang()
      : this.translateService.getCurrentLang();

    this.setVideoControls();
    this.setupVideoTracks();
    await this.initializePlayer();
    this.setWarningMessage();

    const relatedEntityId = this.relatedEntityId();

    if (relatedEntityId) {
      this.progress = this.videoProgressTrackerService.getVideoProgressSubject(relatedEntityId, this.video()?.id);
    }
  }

  private setWarningMessage(): void {
    const video = this.video();
    const showWarning = video && video.checkStreamFile && !video.stream;
    this.warningMessage = (showWarning && this.conversionInProgressWarning) || '';
  }

  private markVideoAsWatched(): void {
    if (this.videoWatched) {
      return;
    }

    this.videoWatched = true;
    this.watched.emit();
  }

  private enableVideoProgressControl(): void {
    const controls: string[] = this.options?.controls as string[];

    if (!controls) {
      return;
    }

    const progressControlIndex = controls.indexOf('progress');

    if (progressControlIndex !== -1) {
      return;
    }

    const index = controls.indexOf('play');

    if (index !== -1) {
      controls.splice(index + 1, 0, 'progress');
      this.options = { ...this.options };
    }
  }

  private keyFramesHandler(): void {
    const currentTime = this.plyr.currentTime;
    const keyframes = this.interactiveData.script?.keyframes || [];
    this.nextKeyFrame = this.getNextKeyFrame(keyframes, currentTime);

    if (this.nextKeyFrameDelay) {
      this.nextKeyFrameDelay.unsubscribe();
    }

    if (this.nextKeyFrame) {
      const secondsUntilNextFrame = this.nextKeyFrame.timestamp - this.plyr.currentTime;
      this.nextKeyFrameDelay = of(true).pipe(
        take(1),
        delay(secondsUntilNextFrame * 1000),
        tap(() => {
          this.isKeyFrameActive = true;
          this.keyFrameAction(this.nextKeyFrame);
        }),
      ).subscribe();
    }
  }

  private getNextKeyFrame(keyFrames: any[], currentTime: number): any {
    for (const item of keyFrames) {
      if (item.timestamp > currentTime) {
        return item;
      }
    }
  }

  private async initializePlayer(): Promise<void> {
    if (!this.settingsLoaded) {
      return;
    }

    if (this.preventVideoLoad) {
      this.videoSources = [];
      this.initialized.next(true);
      return;
    }

    const src = this.src();

    if (!src) {
      this.destroyPlyrInstance();
      this.destroyHlsInstance();
      this.initialized.next(!!this.embed);
      return;
    }

    const videoElement = this.videoPlayer()?.nativeElement;
  
    if (!videoElement) {
      return;
    }

    this.videoSources = [{
      src,
      type: this.provider() === VideoProvider.HLS ? 'application/x-mpegURL' : this.type() ?? ''
    }];

    // Enable cross-origin
    videoElement.setAttribute('crossorigin', 'anonymous');
    this.setVideoControls();
    // Disable iOS native fullscreen to allow custom controls
    if (this.options?.fullscreen) {
      this.options.fullscreen.iosNative = !Capacitor.isNativePlatform();
    }

    // Initialize Plyr
    this.initializePlyrInstance();
    this.initialized.next(true);

    await this.updatePlyrSources();
    this.updatePoster();
    this.initializePlaybackState();
    this.setupVideoCaptionOpacitySync();
  }

  private initializePlaybackState(init = true): void {
    if (!init) {
      this.plyr.muted = false;
      this.plyr.pause();
    }

    if (this.hideControls() && !this.autoplayConfig.enabled) {
      this.plyr.volume = 1;
      this.plyr.speed = 1;
    }

    if (this.plyr.volume === 0 && this.plyr.muted) {
      this.plyr.volume = 1;
    }

    if (this.autoplayConfig.enabled) {
      this.plyr.muted = this.autoplayConfig?.muted ?? true;
      this.plyr.volume = this.autoplayConfig?.volume ?? 0;
      this.plyr.speed = this.autoplayConfig?.speed ?? 1;
      this.plyr.play();
    }
  }

  private initializePlyrInstance(): void {
    const videoElement = this.videoPlayer()?.nativeElement;
    this.plyr = new Plyr(videoElement, this.options);
    this.addEventListeners();

    if (this.videoTracks?.length) {
      this.plyr.language = this.contentLanguage;
    }

    if (!this.allowChangingVideoPlaybackSpeed) {
      this.plyr.speed = 1;
    }
  }

  private addEventListeners(): void {
    // Event Listeners
    this.plyr.on('play', () => this.videoStarted());
    this.plyr.on('playing', () => this.startOrResume());
    this.plyr.on('canplay', () => this.plyrCanPlay());
    this.plyr.on('pause', () => this.pauseOrFinish());
    this.plyr.on('ended', () => this.end());
    this.plyr.on('timeupdate', () => this.timeUpdate());
    this.plyr.on('loadeddata', () => this.loadedData());
    this.plyr.on('loadedmetadata', () => {
      const [width, height] = this.plyr.ratio?.split(':')?.map(Number) ?? [];
      const ratio = width && height ? `${width} / ${height}` : null;

      if (ratio) {
        this.mediaRatioLoaded.emit(ratio);
      }
    });

    this.plyr.on('enterfullscreen', () => {
      this.renderer.addClass(document.body, 'enable-video-fullscreen');
      this.toggleFullscreen.emit();
    })

    this.plyr.on('exitfullscreen', () => {
      this.renderer.removeClass(document.body, 'enable-video-fullscreen');
      this.toggleFullscreen.emit();
    })
  }

  private resetPlayer(): void {
    this.showPlayer.set(false);

    setTimeout(() => {
      this.showPlayer.set(true);
    });
  }

  private async updatePlyrSources(): Promise<void> {
    const videoElement = this.videoPlayer()?.nativeElement!;

    if (this.provider() === VideoProvider.HLS) {
      await this.initializePlyrHlsjs(videoElement);
      return;
    }

    this.destroyHlsInstance();

    videoElement.src = this.src() ?? ''; 

    if (this.defaultDriver) {
      this.defaultDriver.updateSource(this.plyr, this.videoSources, videoElement);
      return;
    }

    this.plyr.source = {
      type: 'video',
      title: '',
      sources: this.videoSources,
      tracks: this.videoTracks
    }
  }

  private startOrResume() {
    if (!this.interactiveData || (this.interactiveData && !this.interactiveData.active)) {
      return;
    }

    this.keyFramesHandler();
  }

  private pauseOrFinish() {
    this.paused.emit();
    if (
      !this.interactiveData
      || (this.interactiveData && !this.interactiveData.active)
      || this.isKeyFrameActive
    ) {
      return;
    }

    if (this.nextKeyFrameDelay) {
      this.nextKeyFrameDelay.unsubscribe();
    }
  }

  private plyrCanPlay(): void {
    this.videoCanPlay.emit();
  }

  private loadedData(): void {
    if (!this.plyr) {
      return;
    }

    this.videoDurationInitialized.emit(this.plyr.duration);
  }

  private setupVideoTracks(): void {
    if (!this.video()) {
      this.revokeVideoTrackUrls();
      this.videoTracks = [];
      this.removeManagedVideoTrackElements(this.videoPlayer()?.nativeElement);
      return;
    }

    const { subtitles = [] } = this.video() ?? {};

    this.revokeVideoTrackUrls();
    this.videoTracks = subtitles.map(item => {
      const keys = Object.keys(item);

      if (keys.length === 0) {
        return;
      }

      const lang = keys[0];
      const blob = new Blob([item[lang]], { type: 'text/vtt' });

      return {
        kind: 'captions',
        label: this.languageStorageService.getLanguageLabel(lang),
        srcLang: lang,
        src: URL.createObjectURL(blob),
        default: this.contentLanguage === lang
      } as Plyr.Track;
    }).filter(item => !!item);

    this.appendVideoTracks(this.videoPlayer()?.nativeElement);
  }

  private appendVideoTracks(videoElement?: HTMLMediaElement): void {
    if (!videoElement) {
      return;
    }

    this.removeManagedVideoTrackElements(videoElement);

    this.videoTracks?.forEach(plyrTrack => {
      const track = document.createElement('track');
      Object.assign(track, {
        ...plyrTrack,
        srclang: plyrTrack.srcLang,
      });
      track.setAttribute(this.managedVideoTrackAttribute, 'true');

      videoElement.appendChild(track);
    });
  }

  private removeManagedVideoTrackElements(videoElement?: HTMLMediaElement): void {
    if (!videoElement) {
      return;
    }

    videoElement.querySelectorAll<HTMLTrackElement>(`track[${this.managedVideoTrackAttribute}]`).forEach(track => track.remove());
  }

  private revokeVideoTrackUrls(): void {
    this.videoTracks?.forEach(track => {
      if (track.src?.startsWith('blob:')) {
        URL.revokeObjectURL(track.src);
      }
    });
  }

  private timeUpdate(): void {
    if (this.videoCompleted()) {
      return;
    }

    if (!this.plyr) {
      return;
    }

    const { currentTime, duration } = this.plyr;

    if (!currentTime || !duration) {
      return;
    }

    const threshold = 1;

    if (duration - currentTime <= threshold) {
      this.end(false);
      this.progress.next(duration);
      return;
    }

    this.progress.next(currentTime);
  }

  private setVideoControls(): void {
    const optionsTemp: any = {
      volume: this.volume(),
      clickToPlay: this.clickToPlay(),
      settings: ['quality', 'captions'],
      storage: {
        enabled: !this.hideControls() && !this.autoplayConfig.enabled
      },
      keyboard: {
        focused: false,
        global: false
      },
      fullscreen: {
        iosNative: true
      },
      captions: {
        active: true,
        update: true
      }
    };

    if (this.allowChangingVideoPlaybackSpeed) {
      optionsTemp.settings.push('speed');
      Object.assign(optionsTemp, {
        speed: {
          selected: 1,
          options: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]
        }
      });
    }

    const additionalOptions = this.additionalOptions();

    if (additionalOptions) {
      Object.assign(optionsTemp, additionalOptions)
    }

    if (this.isAdmin) {
      this.options = { ...this.options, ...optionsTemp };
      return;
    }

    let controls = this.controls().slice();

    if (this.requiredToWatch() && !this.videoCompleted()) {
      controls = controls?.filter(item => item !== 'progress');
    }

    if (controls !== undefined) {
      optionsTemp.controls = controls;
    }

    if (this.interactiveData && this.interactiveData.active) {
      optionsTemp.controls = [];
      optionsTemp.clickToPlay = true;
    }

    this.options = { ...this.options, ...optionsTemp };;
  }

  private parseScript(script: string): any {
    const json: any = yaml.load(script);

    if (!json) {
      return;
    }

    json.videoscript.keyframes.forEach((keyframe: any) => {
      keyframe.timestamp = this.keyFrameTimeToSeconds(keyframe.timestamp);

      if (keyframe.action?.timestamp) {
        keyframe.action.timestamp = this.keyFrameTimeToSeconds(keyframe.action.timestamp);
      }

      keyframe.decisions?.forEach((decision: any) => {
        decision.action.timestamp = this.keyFrameTimeToSeconds(decision.action.timestamp);
      });
    });

    return json.videoscript;
  }

  private keyFrameTimeToSeconds(time: string): number {
    const [hours, minutes, seconds] = time.split(':');
    return +seconds + (+minutes * 60) + (+hours * 60 * 60);
  }

  private getVideoDataByUrl(url: string): { id: string; hash?: string; start?: number, provider: string } | undefined {
    const youtubeRegex = this.isYoutubeUrl(url);
    const vimeoRegex = this.isVimeoUrl(url);

    if (youtubeRegex) {
      const id = (youtubeRegex && youtubeRegex[1].length === 11) ? youtubeRegex[1] : '';
      const queryParams: any = youtubeRegex[0].split('?')[1]?.split('&')?.reduce((prev, param) => {
        const [name, value] = param.split('=');
        prev[name] = value;
        return prev;
      }, {});

      const start = queryParams?.start || queryParams?.t;

      return { id, provider: 'youtube', start: start ? Number.parseInt(start) : null };
    } else if (vimeoRegex) {
      const id = vimeoRegex[3] || '';
      const hash = vimeoRegex[4] || vimeoRegex[5] || '';

      return { id, hash, provider: 'vimeo' };
    }

    return;
  }

  private destroyHlsInstance(): void {
    if (this.hls) {
      this.hls.detachMedia();
      this.hls.destroy();
      this.hls = null;
    }
  }

  private destroyPlyrInstance(): void {
    if (this.plyr) {
      this.plyr.destroy();
      this.plyr = null;
    }
  }

  private createHlsjsIntance(): Hls {
    if (this.hls) {
      return this.hls;
    }

    const hls = new Hls({
      startLevel: -1,
      testBandwidth: false,
      maxMaxBufferLength: 30, // Prevent excessive buffering
      capLevelToPlayerSize: false, // Adjust based on player size
      abrEwmaFastLive: 3, // Faster adaptation in live streams
      abrEwmaDefaultEstimate: this.getInitialHlsBandwidthEstimate(),
      xhrSetup: function (xhr, _url) {
        xhr.withCredentials = true;
      }
    });

    this.saveHlsBandwidthEstimate(hls);

    return hls;
  }

  private async attachHlsMedia(videoElement: HTMLMediaElement): Promise<void> {
    const src = this.src();

    if (!this.hls || !src) {
      return;
    }
  
    if (this.hls?.media) {
      this.hls.loadSource(src);
      return;
    }

    return await new Promise<void>((resolve) => {
      this.hls?.once(Hls.Events.MEDIA_ATTACHED, () => {
        this.hls?.loadSource(src);
        resolve();
      });
      this.hls?.attachMedia(videoElement);
    });
  }

  private async initializePlyrHlsjs(videoElement: HTMLMediaElement): Promise<void> {
    return this.ngZone.runOutsideAngular(async () => {
      this.hls = this.createHlsjsIntance();
  
      await this.attachHlsMedia(videoElement);

      const level = await this.setQualityAndRetrieveMaxResolution(this.hls);

      this.ngZone.run(() => this.appendVideoTracks(videoElement));

      if (level) {
        setTimeout(() => {
          this.ngZone.run(() => {
            this.videoMaxResolutions.set(level);
          });
        })
      }
    });
  }

  private getInitialHlsBandwidthEstimate(): number {
    try {
      const raw = sessionStorage.getItem(this.storageKeys.HLS_BANDWIDTH_ESTIMATE);
      const parsed = raw !== null ? Number(raw) : NaN;

      return Number.isFinite(parsed) ? parsed : this.defaultHlsBandwidthEstimate;
    } catch {
      return this.defaultHlsBandwidthEstimate;
    }
  }
  

  private saveHlsBandwidthEstimate(hls: Hls): void {
    let lastSave = 0;

    hls.on(Hls.Events.FRAG_LOADED, () => {
      const now = Date.now();

      if (now - lastSave < this.hlsBandwidthEstimateSaveInterval) {
        return;
      }

      const bandwidthEstimate = hls.bandwidthEstimate;
      if (!Number.isFinite(bandwidthEstimate)) {
        return;
      }

      try {
        sessionStorage.setItem(this.storageKeys.HLS_BANDWIDTH_ESTIMATE, String(bandwidthEstimate));
      } catch {
        // Ignore unavailable storage and keep adaptive playback running.
      }

      lastSave = now;
    });
  }

  private setQualityAndRetrieveMaxResolution(hls: Hls): Promise<Level | null> {
    return new Promise((resolve) => {
      const timeout = setTimeout(() => resolve(null), 2000);

      hls.once(Hls.Events.MANIFEST_PARSED, () => {
        const availableQualities = hls.levels.map((l) => l.height);
        availableQualities.unshift(0);
        const maxLevel = hls.levels.slice().reverse()?.[0];

        Object.assign(this.options, {
          quality: {
            default: 0,
            options: availableQualities,
            forced: true,
            onChange: (quality: number) => {
              if (quality === 0) {
                hls.currentLevel = -1;
                return;
              }

              const index = hls.levels.findIndex(level => level.height === quality);
              if (index !== -1) {
                hls.currentLevel = index;
              }
            },
          },
          i18n: {
            qualityLabel: { 0: 'Auto' },
          },
        });

        clearTimeout(timeout);
        resolve(maxLevel);
      });
    });
  }

  private getExplicitContainerDimension(dimension: 'width' | 'height'): number | null {
    if (!this.element?.nativeElement) {
      return null;
    }

    const computedStyle = window.getComputedStyle(this.element.nativeElement);
    const value = computedStyle[dimension];

    return value && value !== 'auto' ? parseFloat(value) : null;
  }

  private getExplicitContainerHeight(): number | null {
    return this.getExplicitContainerDimension('height');
  }

  private getExplicitContainerWidth(): number | null {
    return this.getExplicitContainerDimension('width');
  }

  private setVideoCssProperties(width: number, height: number): void {
    this.element.nativeElement.style.setProperty('--lms-video-max-width', width ? `${width}px` : 'unset');
    this.element.nativeElement.style.setProperty('--lms-video-max-height', height ? `${height}px` : 'unset');
  }

  private setVideoPlayerSize(): void {
    if (!this.embed || !this.videoPlayer()) {
      return;
    }

    setTimeout(() => {
      const containerEl = this.videoPlayer()?.nativeElement;
  
      if (!containerEl) {
        return;
      }

      this.renderer.removeStyle(containerEl, 'max-width');
      this.maxEmbedWidth = Math.floor(containerEl.offsetHeight * (16 / 9));

      if (containerEl.offsetWidth - this.maxEmbedWidth > 2) {
        this.renderer.setStyle(containerEl, 'max-width', `${this.maxEmbedWidth}px`);
      }
    });
  }

  private isYoutubeUrl(url: string): RegExpMatchArray | null {
    return url.match(this.regexList.youtube);
  }

  private isVimeoUrl(url: string): RegExpMatchArray | null {
    return url.match(this.regexList.vimeo);
  }

  private normalizeAutoplay(value: boolean | AutoplayConfig): AutoplayConfig {
    if (typeof value === 'boolean') {
      return {
        enabled: value
      };
    }

    return {
      enabled: value?.enabled ?? false,
      muted: value?.muted ?? true,
      volume: value?.volume ?? 0,
      speed: value?.speed ?? 1
    };
  }

  private scheduleInitialization(): void {
    clearTimeout(this.initializeTimeout);

    this.initializeTimeout = setTimeout(() => {
      this.initializePlayer();
    }, 200);
  }

  private updatePoster(): void {
    if (!this.initialized.value || !this.plyr) {
      return;
    }

    if (!this._poster?.uri) {
      this.plyr.poster = '';
      return;
    }

    const videoElement = this.videoPlayer()?.nativeElement;

    if (!videoElement) {
      return;
    }

    const posterUrl$ = this.cloudImageService.generateCloudImageResourceUrl(this._poster?.uri, null, this._poster?.additionalMediaProps, this._poster.crop);

    posterUrl$.subscribe(url => {
      this.plyr.poster = url;
    });
  }

  private setupVideoCaptionOpacitySync(): void {
    const video = this.plyr.elements?.wrapper?.querySelector('video');

    if (!video) {
      return;
    }

    video.addEventListener('webkitpresentationmodechanged', () => {
      const mode = (video as any)?.webkitPresentationMode;
      const isFullscreen = mode === 'fullscreen';

      this.element.nativeElement?.style?.setProperty('--lms-native-video-captions-opacity', isFullscreen ? 1 : 0);
    });
  }

  ngOnDestroy(): void {
    this.mediaRatioLoaded.emit();
    this.destroyHlsInstance();
    this.destroyPlyrInstance();
    this.revokeVideoTrackUrls();
    this.progress?.complete();
  }

}
